import { useMemo } from "react";
import type { NurseResponse } from "@vTypes/nurse-type";

interface UseNurseEditLogicProps {
  nurseData?: NurseResponse;
  allNurses: NurseResponse[];
}

// 전담여부 토글 로직
export const toggleNightNurseLogic = (
  currentShifts: string[],
  type: string
) => {
  if (currentShifts.includes(type)) {
    return currentShifts.filter((t) => t !== type);
  }
  if (currentShifts.length < 2) {
    return [...currentShifts, type];
  }
  return currentShifts;
};

export const useNurseEditLogic = ({
  nurseData,
  allNurses,
}: UseNurseEditLogicProps) => {
  // 현재 간호사가 프리셉터인지, 프리셉티인지 확인

  const preceptorStatus = useMemo(() => {
    if (!nurseData) return { isPreceptor: false, isPreceptee: false };

    const isPreceptee = !!nurseData.preceptor_id;
    const isPreceptor = allNurses.some(
      (nurse) => nurse.preceptor_id === nurseData.nurse_id
    );

    return { isPreceptor, isPreceptee };
  }, [nurseData, allNurses]);

  // 프리셉티 목록 (현재 간호사가 프리셉터인 간호사들)
  const currentPreceptees = useMemo(() => {
    if (!nurseData) return [];
    return allNurses
      .filter((nurse) => nurse.preceptor_id === nurseData.nurse_id)
      .map((nurse) => nurse.nurse_id);
  }, [nurseData, allNurses]);

  // MultiSelect 옵션 - 프리셉티로 선택 가능한 간호사 목록
  const availablePreceptees = useMemo(() => {
    if (!nurseData) return [];

    return allNurses
      .filter((nurse) => {
        // 자기 자신 제외
        if (nurse.nurse_id === nurseData.nurse_id) return false;
        // 비활성 간호사 제외
        if (nurse.active === 0) return false;
        // 이미 다른 프리셉터가 있는 간호사 제외
        if (nurse.preceptor_id && nurse.preceptor_id !== nurseData.nurse_id)
          return false;
        // 본인의 프리셉터인 간호사 제외 (순환 참조 방지)
        if (nurseData.preceptor_id === nurse.nurse_id) return false;
        // 이미 프리셉터인 간호사 제외 (다른 사람의 프리셉터인 경우 프리셉티 후보에서 제외)
        if (allNurses.some((n) => n.preceptor_id === nurse.nurse_id))
          return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, "ko"))
      .map((nurse) => ({
        label: nurse.name,
        value: nurse.nurse_id,
      }));
  }, [nurseData, allNurses]);

  // 프리셉터 목록 계산: 프리셉터로 지정 가능한 간호사 목록
  const preceptorList = useMemo(() => {
    if (!nurseData) return [];
    return allNurses
      .filter((nurse) => {
        // 본인 제외
        if (nurse.nurse_id === nurseData.nurse_id) return false;
        // 이미 프리셉티인 간호사 제외
        if (nurse.preceptor_id) return false;
        // 비활성 간호사 제외
        if (nurse.active === 0) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
  }, [allNurses, nurseData]);

  return {
    preceptorStatus,
    currentPreceptees,
    availablePreceptees,
    preceptorList,
  };
};
